/*
  The grade scale SetViewTable renders as columns and FilterGradeDDL offers as options.

  It used to be two hand-maintained lists: sixteen column descriptors in SetViewTable and
  fifteen <MenuItem>s in FilterGradeDDL, which never had to agree because the dropdown was
  dead. D18 wires it up, and the predicate behind it (filterCardsGraded, at the foot of
  this file) matches `String(row.grade) === gradeValue` against the API's finalGradeCounts
  — so a dropdown offering a grade string no column reads would silently filter every card
  away. One list, so they cannot drift.

  D28 closed the last gap: the dropdown now offers all sixteen, Authenticated included,
  labelled "A" exactly as its column header is. One list, one label per grade.

  `grade` is the value the API carries, not the label. "Gold 10.0" and "Gem Mint 10.0" are
  grade names rather than numbers, which is also why the grade columns cannot be sorted by
  their label and sort by their count instead (§6.4).
*/

export interface GradeColumnSpec {
  /* The descriptor id: the sort key stored in orderBy, and the column's React key. */
  id: string;
  /* The value in a finalGradeCounts row's `grade` field, and what the dropdown emits. */
  grade: string;
  label: string;
  /* A shorter header for the table, where the full label would cost the row its fit. */
  short?: string;
  /* The lower half of the scale: hidden until "Show All Grades". */
  lower?: boolean;
}

export const SET_VIEW_GRADE_COLUMNS: readonly GradeColumnSpec[] = [
  { id: "pristine_ten_gold", grade: "Gold 10.0", label: "Flawless 10 (Quad)", short: "Flawless 10" },
  { id: "pristine_ten", grade: "10.0", label: "Pristine 10" },
  { id: "gem_mint_ten", grade: "Gem Mint 10.0", label: "Gem Mint 10" },
  { id: "nine_five", grade: "9.5", label: "9.5" },
  { id: "nine", grade: "9.0", label: "9" },
  { id: "eight_five", grade: "8.5", label: "8.5" },
  { id: "eight", grade: "8.0", label: "8" },
  { id: "seven_five", grade: "7.5", label: "7.5" },
  { id: "seven", grade: "7.0", label: "7" },
  { id: "six", grade: "6.0", label: "6", lower: true },
  { id: "five", grade: "5.0", label: "5", lower: true },
  { id: "four", grade: "4.0", label: "4", lower: true },
  { id: "three", grade: "3.0", label: "3", lower: true },
  { id: "two", grade: "2.0", label: "2", lower: true },
  { id: "one", grade: "1.0", label: "1", lower: true },
  { id: "authenticated", grade: "0.0", label: "A" },
];

/*
  The lower half as a lookup, for the one caller that needs to ask "is the column the
  table is sorted by about to disappear?" rather than "which columns show now".
*/
export const LOWER_GRADE_COLUMN_IDS: ReadonlySet<string> = new Set(
  SET_VIEW_GRADE_COLUMNS.filter((spec) => spec.lower === true).map((spec) => spec.id)
);

/* The columns showing at this setting. "Show All Grades" adds the lower half. */
export const visibleGradeColumns = (showAll: boolean): GradeColumnSpec[] =>
  SET_VIEW_GRADE_COLUMNS.filter((spec) => showAll || spec.lower !== true);

/* FilterGradeDDL's options, in Select's shape. The empty option is added there. */
export const GRADE_FILTER_OPTIONS: readonly { value: string; label: string }[] =
  SET_VIEW_GRADE_COLUMNS.map((spec) => ({
    value: spec.grade,
    label: spec.label,
  }));

/*
  Rows this predicate can read: a set row from v1 /gameInfo carries no counts, a card row
  from v2 /products does. Task 18 moved it here from helpers/tables.tsx, whose other nine
  exports had no call sites left and went with the file.
*/
export interface GradedRow {
  cardsGraded: number | null;
  finalGradeCounts?: { grade: string | number }[] | null;
}

/*
  The grade dropdown's predicate (D18). Called with no grade it degrades to "has been
  graded at all", which is how selectSearchOptions uses it to drop never-graded sets.
*/
export const filterCardsGraded =
  (gradeValue: string | null = null) =>
  (row: GradedRow): boolean => {
    if (gradeValue) {
      return (
        row.cardsGraded !== null &&
        !!row.finalGradeCounts &&
        row.finalGradeCounts.length > 0 &&
        row.finalGradeCounts.some((count) => String(count.grade) === gradeValue)
      );
    }
    return row.cardsGraded !== null;
  };
